import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ArrowLeft, Calendar, ChevronLeft, ChevronRight } from "lucide-react";

export default function DigestDetail() {
  const { digestId } = useParams();
  const navigate = useNavigate();
  const [digest, setDigest] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const targets = [];
      if (digestId) targets.push(api.get(`/digests/${digestId}`));
      else targets.push(api.get("/digest/latest"));
      targets.push(api.get("/digests", { params: { limit: 24 } }));
      const [current, list] = await Promise.all(targets);
      setDigest(current.data);
      setHistory(list.data || []);
    } catch (e) { toast.error(errText(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [digestId]);

  // Keyboard shortcuts: ←/→ jump between adjacent weeks. Ignored inside form fields.
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
      if (e.key === "ArrowLeft" && prev) navigate(`/digest/${prev.id}`);
      else if (e.key === "ArrowRight" && next) navigate(`/digest/${next.id}`);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!digest && !loading) return null;
  const d = digest || {};
  const currentIdx = digestId ? history.findIndex((h) => h.id === digestId) : 0;
  const prev = currentIdx >= 0 && currentIdx < history.length - 1 ? history[currentIdx + 1] : null;
  const next = currentIdx > 0 ? history[currentIdx - 1] : null;

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="digest-detail-page">
      <div>
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-2">
          <ArrowLeft size={12} /> Dashboard
        </Link>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Weekly content digest</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold flex items-center gap-2 mt-1">
              <Calendar size={22} /> Week of {d.week_of}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" asChild disabled={!prev}>
              <Link to={prev ? `/digest/${prev.id}` : "#"} data-testid="digest-prev-btn">
                <ChevronLeft size={14} className="mr-1" /> Older
              </Link>
            </Button>
            {history.length > 0 && (
              <span className="text-[11px] text-muted-foreground font-mono px-1"
                    data-testid="digest-position-indicator" title="Use ← / → to jump between weeks">
                {currentIdx >= 0 ? currentIdx + 1 : "?"} / {history.length}
              </span>
            )}
            <Button size="sm" variant="outline" asChild disabled={!next}>
              <Link to={next ? `/digest/${next.id}` : "#"} data-testid="digest-next-btn">
                Newer <ChevronRight size={14} className="ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              ["Scheduled next 7 days", d.scheduled_count],
              ["Awaiting review", d.pending_count],
              ["Changes requested", d.changes_requested_count],
              ["Inbox awaiting reply", d.unreplied_count],
              ["Published last week", d.published_last_week],
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg border p-3 text-center bg-muted/30">
                <div className="text-3xl font-heading font-semibold">{v ?? 0}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{k}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {d.top_post && (
        <Card>
          <CardHeader><CardTitle className="text-base">Last week's top post 🏆</CardTitle></CardHeader>
          <CardContent className="pt-0 flex gap-4 items-start flex-wrap">
            {d.top_post.image && <img src={d.top_post.image} alt="" className="w-24 h-24 rounded-lg object-cover" />}
            <div className="min-w-0 flex-1">
              <div className="font-heading text-base">{d.top_post.title}</div>
              <div className="text-xs text-muted-foreground capitalize">{d.top_post.platform}{d.top_post.brand ? ` · ${d.top_post.brand}` : ""}</div>
              <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                <span className="rounded-full bg-muted px-2 py-0.5"><b>{d.top_post.likes}</b> likes</span>
                <span className="rounded-full bg-muted px-2 py-0.5"><b>{d.top_post.comments}</b> comments</span>
                {d.top_post.plays != null && <span className="rounded-full bg-muted px-2 py-0.5"><b>{d.top_post.plays}</b> plays</span>}
                {d.top_post.reach != null && <span className="rounded-full bg-muted px-2 py-0.5"><b>{d.top_post.reach}</b> reach</span>}
              </div>
              {d.top_post.permalink && (
                <a href={d.top_post.permalink} target="_blank" rel="noopener noreferrer"
                   className="text-xs text-accent font-semibold mt-2 inline-block">View post →</a>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Coming up this week</CardTitle></CardHeader>
        <CardContent className="pt-0">
          {!d.upcoming?.length ? (
            <p className="text-sm text-muted-foreground">Nothing scheduled — launch a batch in the AI Generator.</p>
          ) : (
            <div className="space-y-1">
              {d.upcoming.map((p) => (
                <div key={p.id} className="flex items-center justify-between border-b border-dashed py-2 text-sm">
                  <div>
                    <div className="font-medium">{p.title}</div>
                    <div className="text-[11px] text-muted-foreground capitalize">{p.platform}{p.brand ? ` · ${p.brand}` : ""}</div>
                  </div>
                  <div className="text-[11px] text-muted-foreground font-mono">{(p.scheduled_at || "").slice(0, 16).replace("T", " · ")}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {history.length > 1 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Digest history</CardTitle></CardHeader>
          <CardContent className="pt-0 grid gap-1 sm:grid-cols-2">
            {history.map((h) => (
              <Link key={h.id} to={`/digest/${h.id}`}
                    className={`text-xs px-2 py-1.5 rounded-md border hover:border-accent ${h.id === d.id ? "border-accent bg-accent/5" : ""}`}>
                Week of {h.week_of}
              </Link>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
